import { useMemo, useState } from 'react'
import type { DrinkEntry } from '@/types'
import {
  PERIOD_OPTIONS,
  computeDailyAggregates,
  computeDayOfWeekStats,
  computeSummaryStats,
  computeTimeOfDayStats,
  computeTopDrinks,
  computeWeeklyStdDrinks,
  filterAggregatesByPeriod,
  filterEntriesByPeriod,
  periodDayCount,
  periodStartDate,
  METRIC_SPECS,
  type Metric,
  type Period,
} from '@/utils/analytics'
import ContributionHeatmap from './ContributionHeatmap'
import PeriodSelector from './PeriodSelector'
import SummaryCards from './SummaryCards'
import WeeklyStandardDrinksChart from './WeeklyStandardDrinksChart'
import DayOfWeekChart from './DayOfWeekChart'
import TimeOfDayChart from './TimeOfDayChart'
import TopDrinksChart from './TopDrinksChart'

interface AnalyticsProps {
  entries: DrinkEntry[]
  onClose: () => void
}

const METRICS: Metric[] = ['bac', 'drinks', 'calories', 'ml']

export default function Analytics({ entries, onClose }: AnalyticsProps) {
  const [period, setPeriod] = useState<Period>('3mo')
  const [metric, setMetric] = useState<Metric>('bac')

  // Compute all aggregates once, then filter for the visible period
  const allAggregates = useMemo(() => computeDailyAggregates(entries), [entries])

  const filteredEntries = useMemo(() => filterEntriesByPeriod(entries, period), [entries, period])
  const filteredAggregates = useMemo(() => filterAggregatesByPeriod(allAggregates, period), [allAggregates, period])
  const dayCount = useMemo(() => periodDayCount(allAggregates, period), [allAggregates, period])

  const summary = useMemo(() => computeSummaryStats(filteredAggregates, dayCount), [filteredAggregates, dayCount])
  const rangeStart = useMemo(() => periodStartDate(allAggregates, period), [allAggregates, period])
  const weeklyStd = useMemo(() => computeWeeklyStdDrinks(filteredAggregates, rangeStart), [filteredAggregates, rangeStart])
  const dowStats = useMemo(() => computeDayOfWeekStats(filteredAggregates, dayCount), [filteredAggregates, dayCount])
  const todStats = useMemo(() => computeTimeOfDayStats(filteredEntries), [filteredEntries])
  const topDrinks = useMemo(() => computeTopDrinks(filteredEntries, 10), [filteredEntries])

  const periodLabel = PERIOD_OPTIONS.find(p => p.id === period)?.label ?? period
  const hasAnyData = entries.some(e => !e.is_gap)

  return (
    <div className="flex-1 p-4 max-w-4xl lg:max-w-6xl mx-auto w-full">
      <div className="mb-6">
        <div className="flex items-center justify-between mb-2">
          <h1 className="text-2xl font-bold text-[var(--text-primary)]">Analytics</h1>
          <button
            onClick={onClose}
            className="text-sm text-[var(--accent-teal)] hover:underline cursor-pointer"
          >
            Back to Dashboard
          </button>
        </div>
        <p className="text-[var(--text-secondary)] text-sm leading-relaxed">
          A day-by-day view of your drinking pattern. The date range applies to every chart on this page.
        </p>
      </div>

      {!hasAnyData ? (
        <div className="card text-center text-sm text-[var(--text-secondary)]">
          Log a drink to start seeing your analytics here.
        </div>
      ) : (
        <div className="space-y-4">
          {/* Page-wide period selector */}
          <div className="flex flex-wrap items-center justify-between gap-2">
            <PeriodSelector period={period} onChange={setPeriod} />
            <p className="text-xs text-[var(--text-secondary)]">
              Showing {periodLabel.toLowerCase()} · {summary.drinkingDays} drinking day{summary.drinkingDays === 1 ? '' : 's'}
            </p>
          </div>

          {/* Top-level stat cards */}
          <SummaryCards stats={summary} />

          {/* Heatmap with its own metric toggle */}
          <div>
            <div className="flex justify-end mb-2">
              <div
                role="tablist"
                aria-label="Heatmap metric"
                className="inline-flex bg-[var(--bg-secondary)] border border-[var(--border)] rounded-lg p-1"
              >
                {METRICS.map(m => {
                  const active = m === metric
                  return (
                    <button
                      key={m}
                      role="tab"
                      aria-selected={active}
                      onClick={() => setMetric(m)}
                      className={
                        'px-3 py-1 text-xs rounded-md transition-colors cursor-pointer ' +
                        (active
                          ? 'bg-[var(--bg-card)] text-[var(--text-primary)] font-semibold'
                          : 'text-[var(--text-secondary)] hover:text-[var(--text-primary)]')
                      }
                    >
                      {METRIC_SPECS[m].shortLabel}
                    </button>
                  )
                })}
              </div>
            </div>
            <ContributionHeatmap aggregates={filteredAggregates} entries={filteredEntries} metric={metric} />
          </div>

          {/* Weekly standard drinks (NHMRC) */}
          <WeeklyStandardDrinksChart weeks={weeklyStd} />

          {/* Day-of-week + time-of-day side by side on desktop */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <DayOfWeekChart stats={dowStats} />
            <TimeOfDayChart stats={todStats} />
          </div>

          {/* Top drinks */}
          <TopDrinksChart drinks={topDrinks} />
        </div>
      )}
    </div>
  )
}
